import { i18n } from "src/boot/i18n"
import { getDB } from "src/db/databases"
import { useCacheStore } from "src/stores/cacheStore"
import { useUserStore } from "src/stores/userStore"
import { Log } from "src/util/classes/log"
import { uploadImages } from "src/util/functions/attachments"
import { getItemClassData } from "src/util/functions/itemClass"
import { showErrorNotification, showSuccessNotification } from "src/util/functions/notifications"
import { getIssuesOnItemList } from "src/util/http/csi/issues"
import {
  getCorrectiveWorkOnItem,
  getCorrectiveWorkOnItemComment,
  getCorrectiveWorkOnItemCommentList,
  getCorrectiveWorkOnItemList,
  getRegularWorkOnItem,
  getRegularWorkOnItemComment,
  getRegularWorkOnItemCommentList,
  getRegularWorkOnItemList,
  patchCorrectiveWorkOnItem,
  patchRegularWorkOnItem,
  postCorrectiveWorkOnItemComment,
  postRegularWorkOnItemComment,
} from "src/util/http/csi/performedWork"
import { AttachmentUpload } from "src/util/types/dataModels/attachment"
import { IssuePriority } from "src/util/types/dataModels/issuePriority"
import { Item } from "src/util/types/dataModels/item"
import {
  CorrectiveWorkOnItem,
  PerformedWorkComment,
  PerformedWorkCommentPayload,
  PerformedWorkOnItemCardData,
  PerformedWorkOnItemTableData,
  RegularWorkOnItem,
} from "src/util/types/dataModels/performedWork"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_PERFORMED_WORK,
} from "src/util/types/dataModels/user"
import { WorkDomainWorkspace } from "src/util/types/dataModels/workDomain"
import { WorkStateOptions } from "../types/components/sfxTable"

export function userHasPermissionToCreatePerformedWork(): boolean {
  const userStore = useUserStore()

  return userStore.checkUserPermission(PERMISSION_PERFORMED_WORK, PERMISSION_ACCESS_CREATE)
}

export function userHasPermissionToUpdatePerformedWork(
  performedWork: CorrectiveWorkOnItem | RegularWorkOnItem
): boolean {
  const userStore = useUserStore()

  return userStore.checkUserPermission(PERMISSION_PERFORMED_WORK, PERMISSION_ACCESS_UPDATE, {
    workDomainUUID: performedWork.work_domain,
  })
}

export async function getLatestCorrectiveWorkOnItem(itemId: string) {
  const db = await getDB()
  const issuesOnItem = await db.issues_on_item
    .find({
      selector: { actionable_item: itemId },
    })
    .exec()

  if (issuesOnItem.length === 0) return

  const issuesIds = issuesOnItem.map((issue) => issue.id)

  const correctiveWork = await db.corrective_work_on_item
    .find()
    .where("issue")
    .in(issuesIds)
    .sort({ declared_dt: "desc", created_dt: "desc" })
    .limit(1)
    .exec()

  if (correctiveWork.length === 0) return

  return correctiveWork[0].toMutableJSON()
}

export async function getLatestRegularWorkOnItem(itemId: string) {
  const db = await getDB()
  const regularWorkOnItem = await db.regular_work_on_item
    .find({
      selector: { actionable_item: itemId },
    })
    .sort({ declared_dt: "desc", created_dt: "desc" })
    .limit(1)
    .exec()

  if (regularWorkOnItem.length === 0) return

  return regularWorkOnItem[0].toMutableJSON()
}

export async function getLatestPerformedWork(itemId: string) {
  const latestCorrectiveWork = await getLatestCorrectiveWorkOnItem(itemId)
  const latestRegularWork = await getLatestRegularWorkOnItem(itemId)

  if (latestCorrectiveWork && latestRegularWork) {
    return new Date(latestCorrectiveWork.declared_dt || latestCorrectiveWork.created_dt).getTime() >
      new Date(latestRegularWork.declared_dt || latestRegularWork.created_dt).getTime()
      ? latestCorrectiveWork
      : latestRegularWork
  } else return latestCorrectiveWork || latestRegularWork
}

export async function filterInvalidWorkDomains(
  workDomains: WorkDomainWorkspace[],
  item: Item | undefined,
  issuePriorityList: IssuePriority[]
): Promise<WorkDomainWorkspace[]> {
  let filteredWorkDomains = workDomains

  // We need only work domains to which the item we are making an issue on can belong
  if (item) {
    const workDomainsAllowedFromItem = await getWorkDomainRestrictionsFromItemClass(item)
    if (workDomainsAllowedFromItem.length > 0) {
      filteredWorkDomains = workDomains.filter((wd) => workDomainsAllowedFromItem.includes(wd.id))
    }
  }

  // There could be potentially a work domain without an issue priority, so we get rid of them
  const workDomainsWithIssuePriorities = new Set(issuePriorityList.map((i) => i.work_domain))
  filteredWorkDomains = filteredWorkDomains.filter((wd) =>
    workDomainsWithIssuePriorities.has(wd.id)
  )

  return filteredWorkDomains
}

async function getWorkDomainRestrictionsFromItemClass(item: Item): Promise<string[]> {
  try {
    const itemClass = await getItemClassData(item.item_class)
    return itemClass?.restricted_to_work_domains || []
  } catch (error) {
    Log.error({
      message: "Could not get item class restrictions to work domains",
      error,
      data: { itemId: item.id, itemClassId: item.item_class },
    })

    // silence the error and throw an error, maybe the user would not choose a bad work order
    return []
  }
}

export async function getCorrectiveWorkOnItemData(
  correctiveWorkId: string
): Promise<CorrectiveWorkOnItem | undefined> {
  try {
    const cacheStore = useCacheStore()
    const correctiveWorkInCache = cacheStore.corrective_work_on_item.get(correctiveWorkId)

    if (correctiveWorkInCache) {
      return correctiveWorkInCache
    } else {
      const db = await getDB()
      const correctiveWorkInDB = await db.corrective_work_on_item.findOne(correctiveWorkId).exec()

      if (correctiveWorkInDB) {
        const correctiveWork = correctiveWorkInDB.toMutableJSON()
        cacheStore.corrective_work_on_item.set(correctiveWork.id, correctiveWork)
        return correctiveWork
      } else {
        return await getCorrectiveWorkOnItem({}, correctiveWorkId)
      }
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching CorrectiveWork data for a given correctiveWorkId",
      error,
      data: { correctiveWorkId: correctiveWorkId },
    })
  }
}

export async function getRegularWorkOnItemData(
  regularWorkId: string
): Promise<RegularWorkOnItem | undefined> {
  try {
    const cacheStore = useCacheStore()
    const regularWorkInCache = cacheStore.regular_work_on_item.get(regularWorkId)

    if (regularWorkInCache) {
      return regularWorkInCache
    } else {
      const db = await getDB()
      const regularWorkInDB = await db.regular_work_on_item.findOne(regularWorkId).exec()

      if (regularWorkInDB) {
        const regularWork = regularWorkInDB.toMutableJSON()
        cacheStore.regular_work_on_item.set(regularWork.id, regularWork)
        return regularWork
      } else {
        return await getRegularWorkOnItem({}, regularWorkId)
      }
    }
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching RegularWork data for a given regularWorkId",
      error,
      data: { regularWorkId: regularWorkId },
    })
  }
}

export async function getRaisedIssuedFromRegularWork(workId: string) {
  try {
    const cacheStore = useCacheStore()
    if (cacheStore.issues_on_item.size > 0) {
      const issuesList = Array.from(cacheStore.issues_on_item.values())
      return issuesList.filter((issue) => issue.raised_from === workId)
    }

    const db = await getDB()
    const issuesInDb = await db.issues_on_item.find().where("raised_from").eq(workId).exec()

    if (issuesInDb.length > 0) {
      return issuesInDb.map((i) => i.toMutableJSON())
    }

    const issues = await getIssuesOnItemList({
      filterParams: { raised_from: workId },
    })
    await db.issues_on_item.bulkInsert(issues.results)

    return issues.results
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching IssueOnItem data",
      error,
    })
  }
}

export async function acceptCorrectiveWorkOnItem(
  correctiveWorkId: string
): Promise<CorrectiveWorkOnItem | undefined> {
  const { t } = i18n.global
  const userStore = useUserStore()

  try {
    const modifiedWork: Partial<CorrectiveWorkOnItem> = {
      concluded_dt: new Date().toJSON(),
      conclusion: "accepted",
      concluded_by_user: userStore.id,
    }

    const acceptedWork = await patchCorrectiveWorkOnItem({}, correctiveWorkId, modifiedWork)
    const db = await getDB()
    await db.corrective_work_on_item.upsert(acceptedWork)

    showSuccessNotification(t("notifications.corrective_work_accepted"))
    return acceptedWork
  } catch (error) {
    showErrorNotification(t("sfxErrorMsg.error_accepting_corrective_work"))
    Log.error({
      message: "An error occurred while accepting the CorrectiveWorkOnItem",
      error,
      data: { correctiveWorkId: correctiveWorkId },
    })
  }
}

export async function acceptRegularWorkOnItem(
  regularWorkId: string
): Promise<RegularWorkOnItem | undefined> {
  const { t } = i18n.global
  const userStore = useUserStore()

  try {
    const modifiedWork: Partial<RegularWorkOnItem> = {
      concluded_dt: new Date().toJSON(),
      conclusion: "accepted",
      concluded_by_user: userStore.id,
    }

    const acceptedWork = await patchRegularWorkOnItem({}, regularWorkId, modifiedWork)
    const db = await getDB()
    await db.regular_work_on_item.upsert(acceptedWork)

    showSuccessNotification(t("notifications.regular_work_accepted"))
    return acceptedWork
  } catch (error) {
    showErrorNotification(t("sfxErrorMsg.error_accepting_regular_work"))
    Log.error({
      message: "An error occurred while accepting the RegularWorkOnItem",
      error,
      data: { regularWorkId: regularWorkId },
    })
  }
}

export async function rejectCorrectiveWorkOnItem(
  correctiveWorkId: string
): Promise<CorrectiveWorkOnItem | undefined> {
  const { t } = i18n.global
  const userStore = useUserStore()

  try {
    const modifiedWork: Partial<CorrectiveWorkOnItem> = {
      concluded_dt: new Date().toJSON(),
      conclusion: "rejected",
      concluded_by_user: userStore.id,
    }

    const rejectedWork = await patchCorrectiveWorkOnItem({}, correctiveWorkId, modifiedWork)
    const db = await getDB()
    await db.corrective_work_on_item.upsert(rejectedWork)

    showSuccessNotification(t("notifications.corrective_work_rejected"))
    return rejectedWork
  } catch (error) {
    showErrorNotification(t("sfxErrorMsg.error_rejecting_corrective_work"))
    Log.error({
      message: "An error occurred while rejecting the CorrectiveWorkOnItem",
      error,
      data: { correctiveWorkId: correctiveWorkId },
    })
  }
}

export async function rejectRegularWorkOnItem(
  regularWorkId: string
): Promise<RegularWorkOnItem | undefined> {
  const { t } = i18n.global
  const userStore = useUserStore()

  try {
    const modifiedWork: Partial<RegularWorkOnItem> = {
      concluded_dt: new Date().toJSON(),
      conclusion: "rejected",
      concluded_by_user: userStore.id,
    }

    const rejectedWork = await patchRegularWorkOnItem({}, regularWorkId, modifiedWork)
    const db = await getDB()
    await db.regular_work_on_item.upsert(rejectedWork)

    showSuccessNotification(t("notifications.regular_work_rejected"))
    return rejectedWork
  } catch (error) {
    showErrorNotification(t("sfxErrorMsg.error_rejecting_regular_work"))
    Log.error({
      message: "An error occurred while rejecting the RegularWorkOnItem",
      error,
      data: { regularWorkId: regularWorkId },
    })
  }
}

export async function saveCorrectiveWorkOnItemComment(
  correctiveWorkId: string,
  commentText: string,
  commentImages?: AttachmentUpload[]
): Promise<PerformedWorkComment | undefined> {
  const userStore = useUserStore()

  try {
    const comment: PerformedWorkCommentPayload = {
      text: commentText,
      user: userStore.id,
      performed_work: correctiveWorkId,
    }

    const commentInServer = await postCorrectiveWorkOnItemComment({}, comment)

    if (commentInServer) {
      if (commentImages && commentImages.length > 0) {
        commentInServer.attachments =
          (await uploadImages("corrective_work_item_comment", commentInServer.id, commentImages)) ||
          []
      }

      const db = await getDB()
      await db.corrective_work_item_comments.insert(commentInServer)

      return commentInServer
    }
  } catch (error) {
    const { t } = i18n.global
    showErrorNotification(t("sfxErrorMsg.error_adding_comment"))

    Log.error({
      message: "An error occurred while saving the CorrectiveWorkOnItem comment/attachments",
      error,
      data: { correctiveWorkId: correctiveWorkId },
    })
  }
}

export async function saveRegularWorkOnItemComment(
  regularWorkId: string,
  commentText: string,
  commentImages?: AttachmentUpload[]
): Promise<PerformedWorkComment | undefined> {
  const userStore = useUserStore()

  try {
    const comment: PerformedWorkCommentPayload = {
      text: commentText,
      user: userStore.id,
      performed_work: regularWorkId,
    }

    const commentInServer = await postRegularWorkOnItemComment({}, comment)

    if (commentInServer) {
      if (commentImages && commentImages.length > 0) {
        commentInServer.attachments =
          (await uploadImages("regular_work_item_comment", commentInServer.id, commentImages)) || []
      }

      const db = await getDB()
      await db.regular_work_item_comments.insert(commentInServer)

      return commentInServer
    }
  } catch (error) {
    const { t } = i18n.global
    showErrorNotification(t("sfxErrorMsg.error_adding_comment"))

    Log.error({
      message: "An error occurred while saving the RegularWorkOnItem comment/attachments",
      error,
      data: { correctiveWorkId: regularWorkId },
    })
  }
}

export async function getCorrectiveWorkCommentListForCorrectiveWorkOnItem(
  correctiveWorkId: string
): Promise<PerformedWorkComment[] | undefined> {
  try {
    const cacheStore = useCacheStore()
    let commentList: PerformedWorkComment[] = []

    if (cacheStore.corrective_work_item_comments.size > 0) {
      for (const commentInCache of cacheStore.corrective_work_item_comments.values()) {
        if (commentInCache.performed_work === correctiveWorkId) {
          commentList.push(commentInCache)
        }
      }
    } else {
      const db = await getDB()
      const commentsInDb = await db.corrective_work_item_comments
        .find({ selector: { performed_work: correctiveWorkId } })
        .exec()

      if (commentsInDb.length > 0) {
        for (const comment of commentsInDb) {
          const commentMutable = comment.toMutableJSON()
          cacheStore.corrective_work_item_comments.set(commentMutable.id, commentMutable)
          commentList.push(comment)
        }
      } else {
        const commentsInServer = await getCorrectiveWorkOnItemCommentList({
          mainParams: undefined,
          filterParams: { performed_work: correctiveWorkId },
        })
        commentList = commentsInServer.results
      }
    }
    return commentList.sort((a, b) => b.created_dt.localeCompare(a.created_dt))
  } catch (error) {
    Log.error({
      message:
        "An error occurred while fetching CorrectiveWorkOnItemComment data for the given CorrectiveWork",
      error,
      data: { correctiveWorkId: correctiveWorkId },
    })
  }
}

export async function getRegularWorkCommentListForRegularWorkOnItem(
  regularWorkId: string
): Promise<PerformedWorkComment[] | undefined> {
  try {
    const cacheStore = useCacheStore()
    let commentList: PerformedWorkComment[] = []

    if (cacheStore.regular_work_item_comments.size > 0) {
      for (const commentInCache of cacheStore.regular_work_item_comments.values()) {
        if (commentInCache.performed_work === regularWorkId) {
          commentList.push(commentInCache)
        }
      }
    } else {
      const db = await getDB()
      const commentsInDb = await db.regular_work_item_comments
        .find({ selector: { performed_work: regularWorkId } })
        .exec()

      if (commentsInDb.length > 0) {
        for (const comment of commentsInDb) {
          const commentMutable = comment.toMutableJSON()
          cacheStore.regular_work_item_comments.set(commentMutable.id, commentMutable)
          commentList.push(comment)
        }
      } else {
        const commentsInServer = await getRegularWorkOnItemCommentList({
          mainParams: undefined,
          filterParams: { performed_work: regularWorkId },
        })
        commentList = commentsInServer.results
      }
    }
    return commentList.sort((a, b) => b.created_dt.localeCompare(a.created_dt))
  } catch (error) {
    Log.error({
      message:
        "An error occurred while fetching RegularWorkOnItemComment data for the given RegularWork",
      error,
      data: { regularWorkId: regularWorkId },
    })
  }
}

export async function getCorrectiveWorkOnItemCommentData(
  commentId: string
): Promise<PerformedWorkComment | undefined> {
  try {
    const cacheStore = useCacheStore()
    const commentInCache = cacheStore.corrective_work_item_comments.get(commentId)

    if (commentInCache) {
      return commentInCache
    } else {
      const db = await getDB()
      const commentInDb = await db.corrective_work_item_comments.findOne(commentId).exec()

      if (commentInDb) {
        const mutableComment = commentInDb.toMutableJSON()
        cacheStore.corrective_work_item_comments.set(mutableComment.id, mutableComment)
        return mutableComment
      } else {
        return await getCorrectiveWorkOnItemComment({}, commentId)
      }
    }
  } catch (error) {
    Log.error({
      message:
        "An error occurred while fetching CorrectiveWorkOnItem PerformedWorkComment data for a given commentId",
      error,
      data: { commentId: commentId },
    })
  }
}

export async function getRegularWorkOnItemCommentData(
  commentId: string
): Promise<PerformedWorkComment | undefined> {
  try {
    const cacheStore = useCacheStore()
    const commentInCache = cacheStore.regular_work_item_comments.get(commentId)

    if (commentInCache) {
      return commentInCache
    } else {
      const db = await getDB()
      const commentInDb = await db.regular_work_item_comments.findOne(commentId).exec()

      if (commentInDb) {
        const mutableComment = commentInDb.toMutableJSON()
        cacheStore.regular_work_item_comments.set(mutableComment.id, mutableComment)
        return mutableComment
      } else {
        return await getRegularWorkOnItemComment({}, commentId)
      }
    }
  } catch (error) {
    Log.error({
      message:
        "An error occurred while fetching RegularWorkOnItem PerformedWorkComment data for a given commentId",
      error,
      data: { commentId: commentId },
    })
  }
}

export async function getUnassessedWorkStats(): Promise<
  { corrective: number; regular: number } | undefined
> {
  try {
    const db = await getDB()
    const regular = await db.regular_work_on_item
      .find({ selector: { conclusion: { $not: null } } })
      .exec()
    const corrective = await db.corrective_work_on_item
      .find({ selector: { conclusion: { $not: null } } })
      .exec()
    return { corrective: corrective.length, regular: regular.length }
  } catch (error) {
    Log.error({ message: "Unable to get unassessed performed work", error })
  }
}

export async function getCommentForClosedCorrectiveWorkOnItem(correctiveWorkId: string) {
  const commentList = await getCorrectiveWorkCommentListForCorrectiveWorkOnItem(correctiveWorkId)

  // Since the comment section get disabled for 'concluded' CorrectiveWork,
  // we can take the last comment as the closing comment
  if (commentList && commentList.length > 0) return commentList[0].text
}

export async function getCorrectiveWorkOnItemListForWorkOrder(
  workOrderId: string
): Promise<CorrectiveWorkOnItem[] | undefined> {
  try {
    const cacheStore = useCacheStore()
    let correctiveWorkList: CorrectiveWorkOnItem[] = []

    if (cacheStore.corrective_work_on_item.size > 0) {
      for (const correctiveWorkInCache of cacheStore.corrective_work_on_item.values()) {
        if (correctiveWorkInCache.work_order === workOrderId) {
          correctiveWorkList.push(correctiveWorkInCache)
        }
      }
    } else {
      const db = await getDB()
      const correctiveWorkInDB = await db.corrective_work_on_item
        .find({ selector: { work_order: workOrderId } })
        .exec()

      if (correctiveWorkInDB.length > 0) {
        for (const correctiveWork of correctiveWorkInDB) {
          const correctiveWorkMutable = correctiveWork.toMutableJSON()
          cacheStore.corrective_work_on_item.set(correctiveWorkMutable.id, correctiveWorkMutable)
          correctiveWorkList.push(correctiveWork)
        }
      } else {
        const correctiveWorkInServer = await getCorrectiveWorkOnItemList({
          mainParams: undefined,
          filterParams: { work_order: workOrderId },
        })
        correctiveWorkList = correctiveWorkInServer.results
      }
    }
    return correctiveWorkList
  } catch (error) {
    Log.error({
      message:
        "An error occurred while fetching CorrectiveWorkOnItem list for the given WorkOrderId",
      error,
      data: { workOrderId: workOrderId },
    })
  }
}

/**
 * Counts the amount of performed corrective work relating to a corrective work order.
 */
export async function countPerformedCorrectiveWorkOnWorkOrder(
  workOrderId: string
): Promise<number> {
  try {
    const db = await getDB()
    const records = await db.corrective_work_on_item
      .find({ selector: { work_order: workOrderId } })
      .exec()
    return records.length
  } catch (error) {
    Log.error({
      message:
        "An error occurred while fetching CorrectiveWorkOnItem list for the given WorkOrderId",
      error,
      data: { workOrderId: workOrderId },
    })
    return 0
  }
}

export async function getRegularWorkOnItemListForWorkOrder(
  workOrderId: string
): Promise<RegularWorkOnItem[] | undefined> {
  try {
    const cacheStore = useCacheStore()
    let regularWorkList: RegularWorkOnItem[] = []

    if (cacheStore.regular_work_on_item.size > 0) {
      for (const regularWorkInCache of cacheStore.regular_work_on_item.values()) {
        if (regularWorkInCache.work_order === workOrderId) {
          regularWorkList.push(regularWorkInCache)
        }
      }
    } else {
      const db = await getDB()
      const regularWorkInDB = await db.regular_work_on_item
        .find({ selector: { work_order: workOrderId } })
        .exec()

      if (regularWorkInDB.length > 0) {
        for (const regularWork of regularWorkInDB) {
          const regularWorkMutable = regularWork.toMutableJSON()
          cacheStore.regular_work_on_item.set(regularWorkMutable.id, regularWorkMutable)
          regularWorkList.push(regularWork)
        }
      } else {
        const regularWorkInServer = await getRegularWorkOnItemList({
          mainParams: undefined,
          filterParams: { work_order: workOrderId },
        })
        regularWorkList = regularWorkInServer.results
      }
    }
    return regularWorkList
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching RegularWorkOnItem list for the given WorkOrderId",
      error,
      data: { workOrderId: workOrderId },
    })
  }
}

/**
 * Sort performed work data by its declared_dt || created_dt.
 *
 * This is applicable for both card and table data.
 *
 * @param a
 * @param b
 */
export function sortPerformedWorkData(
  a: PerformedWorkOnItemCardData | PerformedWorkOnItemTableData,
  b: PerformedWorkOnItemCardData | PerformedWorkOnItemTableData
): number {
  return (
    new Date(
      b.performedWorkOnItemData.declared_dt || b.performedWorkOnItemData.created_dt
    ).getTime() -
    new Date(
      a.performedWorkOnItemData.declared_dt || a.performedWorkOnItemData.created_dt
    ).getTime()
  )
}

export async function getPerformedRegularWorkForItem(itemId: string): Promise<RegularWorkOnItem[]> {
  const db = await getDB()
  const regularWorkDocuments = await db.regular_work_on_item
    .find()
    .where("actionable_item")
    .eq(itemId)
    .exec()
  const sortedData = regularWorkDocuments.sort((a, b) => b.created_dt.localeCompare(a.created_dt))
  return sortedData.map((el) => el.toMutableJSON())
}

export async function getPerformedCorrectiveWorkForItem(
  itemId: string
): Promise<CorrectiveWorkOnItem[]> {
  const db = await getDB()
  const issues = await db.issues_on_item.find().where("actionable_item").eq(itemId).exec()

  const correctiveWorkDocuments = []

  for (const issue of issues) {
    const results = await db.corrective_work_on_item.find().where("issue").eq(issue.id).exec()
    if (results.length > 0) correctiveWorkDocuments.push(...results)
  }

  const sortedData = correctiveWorkDocuments.sort((a, b) =>
    b.created_dt.localeCompare(a.created_dt)
  )
  return sortedData.map((el) => el.toMutableJSON())
}

export function getPerformedWorkState(
  work: CorrectiveWorkOnItem | RegularWorkOnItem
): WorkStateOptions {
  switch (work.conclusion) {
    case "not_applicable": {
      return "not_applicable"
    }
    case "accepted": {
      return "accepted"
    }
    case "rejected": {
      return "rejected"
    }
    default: {
      return "unreviewed"
    }
  }
}
